import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import WorkerSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './WorkerSignupLayout';

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};
const sectionLabel = {
  fontSize: '0.78rem', fontWeight: 700, color: NAVY,
  letterSpacing: '0.08em', textTransform: 'uppercase',
  marginBottom: '0.85rem',
};
const divider = { borderTop: '1px solid #EEF0F3', margin: '1.5rem 0' };

export default function WorkerStep4() {
  const navigate    = useNavigate();
  const { t } = useTranslation();
  const resumeRef   = useRef(null);
  const resumeUploadPromiseRef = useRef(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeMsg, setResumeMsg] = useState('');
  const [dragOver,   setDragOver]   = useState(false);
  const [certs,   setCerts]   = useState([{ name: '', expiry: '' }]);
  const [licenses, setLicenses] = useState([{ name: '', expiry: '' }]);
  const [saveError, setSaveError] = useState('');

  // Reload previously-saved certs/licenses/resume so navigating back and then
  // forward again doesn't show blank fields even though this step's data already exists.
  useEffect(() => {
    const loadExisting = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      const [{ data: certRows }, { data: licenseRows }, { data: workerRow }] = await Promise.all([
        // Same order syncRows reads in, so row N on screen is row N when it saves.
        supabase.from('worker_certifications').select('certification_name, expiry_date').eq('worker_id', userId).order('created_at').order('id'),
        supabase.from('worker_licenses').select('license_name, expiry_date').eq('worker_id', userId).order('created_at').order('id'),
        supabase.from('workers').select('resume_url').eq('id', userId).maybeSingle(),
      ]);
      if (certRows && certRows.length > 0) {
        setCerts(certRows.map(c => ({ name: c.certification_name || '', expiry: c.expiry_date || '' })));
      }
      if (licenseRows && licenseRows.length > 0) {
        setLicenses(licenseRows.map(l => ({ name: l.license_name || '', expiry: l.expiry_date || '' })));
      }
      if (workerRow?.resume_url) setResumeUrl(workerRow.resume_url);
    };
    loadExisting();
  }, []);

  // Uploads immediately on selection (rather than deferring to Continue) so the resume
  // is safely saved before the user can navigate away -- matches the pattern already
  // used for the profile photo in this same flow (and for resumes in WorkerDashboard's
  // own My Profile tab).
  const uploadResume = file => {
    setResumeFile(file);
    // Resolves true only when the file is stored AND workers.resume_url came back saved.
    resumeUploadPromiseRef.current = (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return false;

      setResumeUploading(true);
      setResumeMsg('');
      const path = `${userId}/resume/document`;
      const { error: uploadError } = await supabase.storage
        .from('certification-documents')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) {
        setResumeMsg(t('worker.signup.step4.uploadFailed'));
        setResumeUploading(false);
        setResumeFile(null);
        return false;
      }
      const { data: urlData } = supabase.storage.from('certification-documents').getPublicUrl(path);
      const publicUrl = urlData.publicUrl;
      const { data: savedRow, error: rowError } = await supabase.from('workers').update({ resume_url: publicUrl }).eq('id', userId).select('id');
      if (rowError || savedRow?.length !== 1) {
        setResumeMsg(t('worker.signup.step4.uploadFailed'));
        setResumeUploading(false);
        setResumeFile(null);
        return false;
      }
      setResumeUrl(publicUrl);
      setResumeUploading(false);
      return true;
    })();
  };

  const handleResumeDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadResume(file);
  };

  const handleContinue = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id
    if (!userId) { navigate('/worker/signup'); return }

    // A resume upload triggered by uploadResume may still be in flight (upload +
    // DB save are both async) -- wait for it so clicking Continue right after
    // picking a file can't navigate away before the resume is actually saved.
    setSaveError('')
    if (resumeUploadPromiseRef.current) {
      const resumeSaved = await resumeUploadPromiseRef.current;
      // The attempt is finished either way; a failed one is reported once (here and beside the
      // upload box) and dropped, so the worker can pick the file again or continue without it.
      resumeUploadPromiseRef.current = null;
      if (!resumeSaved) { setSaveError(t('worker.signup.step4.saveFailed')); return }
    }

    const certsSaved = await syncRows('worker_certifications', 'certification_name', userId, certs.filter(c => c.name))
    if (!certsSaved) { setSaveError(t('worker.signup.step4.saveFailed')); return }
    const licensesSaved = await syncRows('worker_licenses', 'license_name', userId, licenses.filter(l => l.name))
    if (!licensesSaved) { setSaveError(t('worker.signup.step4.saveFailed')); return }

    navigate('/worker/signup/verify')
  }

  // Makes the worker's rows in `table` match `wanted` without ever deleting first: existing
  // rows are updated in place (only the ones that changed), missing ones are inserted, and
  // surplus ones are removed last, once everything new is confirmed saved. This replaced a
  // delete-then-insert with neither result checked, where a failed insert left the worker
  // with their previous rows gone. Because each attempt re-reads what is actually stored, a
  // retry after any partial failure converges on `wanted` and cannot create duplicates.
  // Every write must hand its rows back; returns false on the first one that doesn't.
  const syncRows = async (table, nameCol, userId, wanted) => {
    const { data: existing, error: readError } = await supabase.from(table)
      .select(`id, ${nameCol}, expiry_date`).eq('worker_id', userId).order('created_at').order('id')
    if (readError || !existing) return false

    for (let i = 0; i < Math.min(existing.length, wanted.length); i++) {
      const cur = existing[i], want = wanted[i]
      if (cur[nameCol] === want.name && (cur.expiry_date || '') === (want.expiry || '')) continue
      const { data, error } = await supabase.from(table)
        .update({ [nameCol]: want.name, expiry_date: want.expiry }).eq('id', cur.id).select('id')
      if (error || data?.length !== 1) return false
    }

    const missing = wanted.slice(existing.length)
    if (missing.length > 0) {
      const { data, error } = await supabase.from(table)
        .insert(missing.map(w => ({ worker_id: userId, [nameCol]: w.name, expiry_date: w.expiry }))).select('id')
      if (error || data?.length !== missing.length) return false
    }

    const surplusIds = existing.slice(wanted.length).map(r => r.id)
    if (surplusIds.length > 0) {
      const { data, error } = await supabase.from(table).delete().in('id', surplusIds).select('id')
      if (error || data?.length !== surplusIds.length) return false
    }
    return true
  }

  const updateCert    = (i, k, v) => setCerts(prev => prev.map((c, j) => j === i ? { ...c, [k]: v } : c));
  const addCert       = () => setCerts(prev => [...prev, { name: '', expiry: '' }]);
  const updateLicense = (i, k, v) => setLicenses(prev => prev.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const addLicense    = () => setLicenses(prev => [...prev, { name: '', expiry: '' }]);

  return (
    <WorkerSignupLayout
      step={4}
      tipText={t('worker.signup.step4.tip')}
      onBack={() => navigate('/worker/signup/trade')}
      onContinue={handleContinue}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('worker.signup.step4.eyebrow')}
      </div>

      <StepPills current={4} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('worker.signup.step4.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('worker.signup.step4.subtitle')}
        </p>

        {/* Resume upload zone */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={sectionLabel}>{t('worker.signup.step4.resumeLabel')} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'rgba(13,27,42,0.45)', fontSize: '0.78rem' }}>{t('worker.signup.step4.optional')}</span></div>

          <div
            onClick={() => resumeRef.current.click()}
            onDrop={handleResumeDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            style={{
              border: `2px dashed ${dragOver ? AMBER : (resumeFile || resumeUrl) ? AMBER : '#D0D4DA'}`,
              borderRadius: '12px', padding: '2rem',
              textAlign: 'center', cursor: 'pointer',
              backgroundColor: dragOver ? 'rgba(245,166,35,0.05)' : 'rgba(13,27,42,0.02)',
              transition: 'border-color 0.15s, background-color 0.15s',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(13,27,42,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 0.75rem', display: 'block' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {resumeFile ? (
              <p style={{ fontSize: '0.9rem', fontWeight: 600, color: NAVY }}>✓ {resumeFile.name}</p>
            ) : resumeUrl ? (
              <p style={{ fontSize: '0.9rem', fontWeight: 600, color: NAVY }}>{t('worker.signup.step4.resumeUploaded')}</p>
            ) : (
              <>
                <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.55)', marginBottom: '4px' }}>
                  {t('worker.signup.step4.dropzoneText')}
                </p>
                <p style={{ fontSize: '0.85rem', color: 'rgba(13,27,42,0.45)' }}>
                  <span style={{ color: AMBER, fontWeight: 600 }}>{t('worker.signup.step4.browseText')}</span>
                  {' '}{t('worker.signup.step4.fileTypesText')}
                </p>
              </>
            )}
          </div>
          {resumeUploading && (
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(13,27,42,0.5)', marginTop: '6px' }}>{t('worker.signup.step4.uploading')}</div>
          )}
          {resumeMsg && (
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#DC2626', marginTop: '6px' }}>{resumeMsg}</div>
          )}
          <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) uploadResume(e.target.files[0]); }} />

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1rem 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#EEF0F3' }} />
            <span style={{ fontSize: '0.8rem', color: 'rgba(13,27,42,0.4)', fontWeight: 500 }}>{t('worker.signup.step4.orDivider')}</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#EEF0F3' }} />
          </div>

          {/* Auto-build banner */}
          <div style={{
            backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
            borderRadius: '10px', padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '0.85rem', color: '#1e40af', fontWeight: 500, lineHeight: 1.5,
          }}>
            <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>ℹ️</span>
            {t('worker.signup.step4.autoBuildBanner')}
          </div>
        </div>

        <div style={divider} />

        {/* Certifications */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={sectionLabel}>{t('worker.signup.step4.certificationsLabel')}</div>
          {certs.map((cert, i) => (
            <div key={i} style={g2}>
              <div>
                <label style={lbl}>{t('worker.signup.step4.certNameLabel')}</label>
                <input style={inp} placeholder={t('worker.signup.step4.certNamePlaceholder')} value={cert.name}
                  onChange={e => updateCert(i, 'name', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>{t('worker.signup.step4.expiryLabel')}</label>
                <input style={inp} type="month" value={cert.expiry}
                  onChange={e => updateCert(i, 'expiry', e.target.value)} />
              </div>
            </div>
          ))}
          <a href="#" onClick={e => { e.preventDefault(); addCert(); }}
            style={{ color: AMBER, textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem' }}>
            {t('worker.signup.step4.addAnotherCert')}
          </a>
        </div>

        <div style={divider} />

        {/* Licenses */}
        <div>
          <div style={sectionLabel}>{t('worker.signup.step4.licensesLabel')}</div>
          {licenses.map((lic, i) => (
            <div key={i} style={g2}>
              <div>
                <label style={lbl}>{t('worker.signup.step4.licenseNameLabel')}</label>
                <input style={inp} placeholder={t('worker.signup.step4.licenseNamePlaceholder')} value={lic.name}
                  onChange={e => updateLicense(i, 'name', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>{t('worker.signup.step4.expiryLabel')}</label>
                <input style={inp} type="month" value={lic.expiry}
                  onChange={e => updateLicense(i, 'expiry', e.target.value)} />
              </div>
            </div>
          ))}
          <a href="#" onClick={e => { e.preventDefault(); addLicense(); }}
            style={{ color: AMBER, textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem' }}>
            {t('worker.signup.step4.addAnotherLicense')}
          </a>
        </div>
      </div>
      {saveError && (
        <p role="alert" data-testid="signup-save-error" style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', fontWeight: 600, color: '#B91C1C', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          {saveError}
        </p>
      )}
    </WorkerSignupLayout>
  );
}
